import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  createComponent,
  ElementRef,
  EnvironmentInjector,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';

import { HttpHeaders } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TableComponent, TableOptions } from '@shared/components';
import { APIService } from '@core/services';
import { PatientService } from '@core/services/patient.service';
import { ePrescription } from '@core/classes/tables/eprescription';
import { EprescriptionDetailsComponent } from './eprescription-details/eprescription-details.component';
import { AddEprescriptionComponent as AddEprescriptionNew } from '@modules/cy/patient/ep/pages/eprescription/add-eprescription/add-eprescription.component';
import { EprescriptionActionsComponent } from './actions/eprescription-actions/eprescription-actions.component';
import { environment } from '@env/environment';
import { PROJECTS } from '@core/constants';
import { TranslateService, Translation } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'ncp-eprescription',
  imports: [TableComponent],
  templateUrl: './eprescription.component.html',
})
export class EprescriptionComponent implements OnInit, OnDestroy {
  @ViewChild('ePrescriptions')
  public ePrescriptionsEl!: TableComponent<ePrescription>;

  @ViewChild('documentPreview')
  private documentPreviewEl?: ElementRef<HTMLElement>;

  private readonly translate = inject(TranslateService);
  protected translations: Translation = this.translate.instant('button');

  protected readonly apiService = inject(APIService);
  protected readonly patientService = inject(PatientService);
  protected renderer2 = inject(Renderer2);

  private appRef = inject(ApplicationRef);
  private environmentInjector = inject(EnvironmentInjector);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);

  private allData: ePrescription[] = [];
  private detailComponentRefs: ComponentRef<EprescriptionDetailsComponent>[] = [];

  protected previewIsActive = signal<boolean>(false);
  protected previewUrl = signal<SafeResourceUrl | null>(null);
  protected previewType = signal<string>('application/pdf');

  private objectUrl: string | null = null;

  protected options = signal<TableOptions<ePrescription>>(
    new TableOptions<ePrescription>({
      id: 'ePrescriptions',
      type: 'ePrescriptions',
      interface: ePrescription,
      autoLoadData: false,
      searchComponent: null,
      extraComponent: environment.project === PROJECTS.PROVIDERS ? AddEprescriptionNew : null,
      actionsComponent:
        environment.project === PROJECTS.PROVIDERS ? EprescriptionActionsComponent : null,
      pagination: {
        itemsPerPage: 25,
        pageNumber: 1,
        totalItems: 0,
      },
    })
  );

  ngOnInit(): void {
    this.getData();

    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('button');
    });
  }

  ngOnDestroy(): void {
    this.destroyDetailComponents();
    this.closePreview();
  }

  protected getData = () => {
    this.apiService
      .call({
        path: '/get-prescription',
        headers: new HttpHeaders().set('Ptoken', this.patientService.pToken || ''),
        body: {
          version_id: this.patientService.patient?.version?.id || -1,
        },
      })
      ?.subscribe((response: any) => {
        if (response && response.status === 1) {
          this.allData = response.data.map((x: any) => {
            const dateOfExpiry = this.calculateExpiryTimestamp(
              x.date_of_onset,
              x.duration,
              x.duration_unit
            );

            const originalRow = {
              ...x,
              date_of_expiry: x.date_of_expiry ?? dateOfExpiry,
              version_id: x.version_id ?? -1,
            };

            return {
              ...x,
              original_row: originalRow,
              date_of_expiry: originalRow.date_of_expiry,
              version_id: originalRow.version_id,
              prescription_id: `${x.prescription_id}`,
              issue_date: this.formatDate(x.date_of_issue),
              expiry_date: this.calculateExpiryDate(x.date_of_onset, x.duration, x.duration_unit),
              description: `(${x.active_ingredient_id}) ${x.active_ingredient} ${x.strength}`,
              status: Number(x.status ?? 1),
              status_desc: this.getStatusDescription(Number(x.status)),
              is_open: false,
            };
          });

          this.setTableOptions(this.allData);
        }
      });
  };

  private setTableOptions(data: ePrescription[]): void {
    const tableOptions = new TableOptions<ePrescription>({
      id: 'ePrescriptions',
      type: 'ePrescriptions',
      interface: ePrescription,
      data,
      autoLoadData: false,
      searchComponent: null,
      extraComponent: environment.project === PROJECTS.PROVIDERS ? AddEprescriptionNew : null,
      actionsComponent:
        environment.project === PROJECTS.PROVIDERS ? EprescriptionActionsComponent : null,
      pagination: {
        itemsPerPage: 25,
        pageNumber: 1,
        totalItems: data.length,
      },
      onRowClick: (row: ePrescription) => {
        row.is_open = !row.is_open;
        this.setTableOptions(this.options().data || []);
      },
      renderCustomRow: (td, row, item) => {
        const data = row as ePrescription;

        if (item === 'status') {
          td.innerHTML = '';

          const span = this.renderer2.createElement('span');

          this.renderer2.addClass(span, 'badge');

          switch (data.status) {
            case 1:
              {
                span.innerText = data.status_desc || 'Unknown';
                this.renderer2.addClass(span, 'bg-success');
              }
              break;

            case 2:
              {
                span.innerText = data.status_desc || 'Unknown';
                this.renderer2.addClass(span, 'bg-danger');
              }
              break;

            case 3:
              {
                span.innerText = data.status_desc || 'Unknown';
                this.renderer2.addClass(span, 'bg-primary');
              }
              break;

            default: {
              span.innerText = data.status_desc || 'Unknown';
              this.renderer2.addClass(span, 'bg-secondary');
            }
          }

          this.renderer2.appendChild(td, span);

          return '';
        }

        return data[item];
      },
    });

    (tableOptions as any).reloadData = this.getData;
    (tableOptions as any).previewPdf = this.previewPdf;
    (tableOptions as any).previewCda = this.previewCda;
    (tableOptions as any).closePreview = this.closePreview;

    this.options.set(tableOptions);

    setTimeout(() => {
      this.renderExpandedRows();
    });
  }

  protected previewPdf = (base64: string): void => {
    this.closePreview();

    if (!base64) {
      return;
    }

    try {
      const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

      const byteChars = atob(cleanBase64);
      const byteNumbers = new Array(byteChars.length);

      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: 'application/pdf',
      });

      this.objectUrl = URL.createObjectURL(blob);

      this.previewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl));

      this.previewType.set('application/pdf');
      this.previewIsActive.set(true);

      this.scrollToPreview();
    } catch (error) {
      console.error('Unable to preview PDF:', error);
      this.closePreview();
    }
  };

  protected previewCda = (base64: string): void => {
    this.closePreview();

    if (!base64) {
      return;
    }

    try {
      const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

      const dataUrl = `data:text/html;base64,${cleanBase64}`;

      this.previewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl));

      this.previewType.set('text/html');
      this.previewIsActive.set(true);

      this.scrollToPreview();
    } catch (error) {
      console.error('Unable to preview CDA:', error);
      this.closePreview();
    }
  };

  protected closePreview = (): void => {
    this.previewIsActive.set(false);
    this.previewUrl.set(null);
    this.previewType.set('application/pdf');

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  };

  private scrollToPreview(): void {
    setTimeout(() => {
      this.documentPreviewEl?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }

  private renderExpandedRows(): void {
    const table = document.getElementById('ePrescriptions');

    if (!table) {
      return;
    }

    this.destroyDetailComponents();

    table.querySelectorAll('.details-row').forEach((x) => x.remove());

    const rows = table.querySelectorAll('tbody tr');

    rows.forEach((tr, index) => {
      const item = this.ePrescriptionsEl.tableData[index];

      if (!item?.is_open) {
        return;
      }

      const detailTr = this.renderer2.createElement('tr');
      this.renderer2.addClass(detailTr, 'details-row');

      const td = this.renderer2.createElement('td');
      this.renderer2.setAttribute(td, 'colspan', '6');

      const componentRef = createComponent(EprescriptionDetailsComponent, {
        environmentInjector: this.environmentInjector,
      });

      componentRef.instance.item = item;

      this.appRef.attachView(componentRef.hostView);
      this.detailComponentRefs.push(componentRef);

      this.renderer2.appendChild(td, componentRef.location.nativeElement);

      this.renderer2.appendChild(detailTr, td);

      tr.insertAdjacentElement('afterend', detailTr);

      componentRef.changeDetectorRef.detectChanges();
    });

    this.changeDetectorRef.detectChanges();
  }

  private destroyDetailComponents(): void {
    this.detailComponentRefs.forEach((ref) => {
      this.appRef.detachView(ref.hostView);
      ref.destroy();
    });

    this.detailComponentRefs = [];
  }

  private getStatusDescription(status: number): string {
    switch (status) {
      case 0:
        return 'Expired';

      case 1:
        return 'Active';

      case 2:
        return 'Deleted';

      case 3:
        return 'Dispensed';

      default:
        return 'Unknown';
    }
  }

  private formatDate(date?: string): string {
    if (!date) {
      return '';
    }

    const d = new Date(date);

    if (isNaN(d.getTime())) {
      return date;
    }

    return d.toLocaleDateString('en-GB');
  }

  private calculateExpiryDate(onset?: string, duration?: string, unit?: string): string {
    const timestamp = this.calculateExpiryTimestamp(onset, duration, unit);

    if (!timestamp) {
      return '';
    }

    return this.formatDate(new Date(timestamp).toISOString());
  }

  private calculateExpiryTimestamp(
    onset?: string,
    duration?: string,
    unit?: string
  ): number | null {
    if (!onset) {
      return null;
    }

    const d = new Date(onset);
    const amount = Number(duration || 0);

    if (isNaN(d.getTime())) {
      return null;
    }

    if (unit === 'day') {
      d.setDate(d.getDate() + amount);
    }

    if (unit === 'week') {
      d.setDate(d.getDate() + amount * 7);
    }

    if (unit === 'month') {
      d.setMonth(d.getMonth() + amount);
    }

    if (unit === 'year') {
      d.setFullYear(d.getFullYear() + amount);
    }

    return d.getTime();
  }
}
